import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Discord icon component
const DiscordIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
  </svg>
);

const DiscordCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, setToken, fetchGuilds } = useAuth();
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // Prevent double execution
    if (processing) return;
    
    const code = searchParams.get("code");
    const errorParam = searchParams.get("error");

    if (errorParam) {
      setError("Discord login was cancelled or failed");
      toast.error("Discord login cancelled");
      setTimeout(() => navigate("/"), 2000);
      return;
    }

    if (!code) {
      setError("No authorization code received");
      toast.error("Invalid Discord callback");
      setTimeout(() => navigate("/"), 2000);
      return;
    }

    const handleCallback = async () => {
      setProcessing(true);
      try {
        const response = await axios.post(`${API_URL}/api/auth/discord/callback`, { code });
        const { token, user } = response.data;
        
        // Store token and update auth state
        localStorage.setItem("token", token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        setToken(token);
        setUser(user);
        
        // Fetch guilds after login
        if (fetchGuilds) {
          await fetchGuilds();
        }
        
        toast.success(`Welcome, ${user.username}!`);
        navigate("/");
      } catch (err) {
        // Don't show error if it's a code reuse issue (user might have already logged in)
        const message = err.response?.data?.detail || "Discord login failed";
        
        // Check if user is already logged in via token
        const existingToken = localStorage.getItem("token");
        if (existingToken) {
          // Already logged in, just redirect
          navigate("/");
          return;
        }
        
        setError(message);
        toast.error(message);
        setTimeout(() => navigate("/"), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setUser, setToken, fetchGuilds, processing]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.9), rgba(15, 23, 42, 0.95)), url('https://images.unsplash.com/photo-1667766528699-d0d2db8e60b9?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="text-center space-y-4">
        {error ? (
          <>
            <div className="w-16 h-16 mx-auto bg-red-500/20 rounded-full flex items-center justify-center">
              <DiscordIcon className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-red-400">Login Failed</h2>
            <p className="text-muted-foreground">{error}</p>
            <p className="text-sm text-muted-foreground">Redirecting to home...</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto bg-[#5865F2]/20 rounded-full flex items-center justify-center">
              <DiscordIcon className="w-8 h-8 text-[#5865F2]" />
            </div>
            <h2 className="text-xl font-semibold">Connecting to Discord</h2>
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-[#5865F2]" />
              <span className="text-muted-foreground">Please wait...</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DiscordCallbackPage;
